/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Heading, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { IImageData, IImageSettings, Image } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { IHeadingData, ILinkData, ISmweFooterItemConfig, ISmweFooterItemProps } from './smwe-footer-item.props.autogenerated';

export interface IFooterViewProps extends ISmweFooterItemProps<ISmweFooterItemConfig> {
    heading: React.ReactNode;
    image: React.ReactNode;
    text: React.ReactNode;
    linkText: React.ReactNode;
    className?:string;
    FooterItem: IModuleProps;
    HeadingContainer: INodeProps;
    ItemContainer: INodeProps;
    Link: INodeProps;
}

/**
 *
 * FooterItem component
 * @extends {React.PureComponent<ISmweFooterItemProps<ISmweFooterItemConfig>>}
 */
export class FooterItem extends React.PureComponent<ISmweFooterItemProps<ISmweFooterItemConfig>> {
    constructor(props: ISmweFooterItemProps<ISmweFooterItemConfig>) {
        super(props);
    }

    public render(): JSX.Element {
        const {heading, image, link, text} = this.props.config;
        const footerItemClassName = classnames('ms-footer__item', this.props.config.className);

        const viewProps: IFooterViewProps = {
            ... this.props,
            heading: this._createHeading(heading!),
            image: this._createImageMarkup(image!),
            text:this._createText(text!),
            linkText: this._createLinkText(link!),
            // @ts-ignore
            FooterItem: {
                moduleProps: this.props,
                className: footerItemClassName
            },
            // @ts-ignore
            HeadingContainer: {
                className: 'ms-footer__heading'
            },
            // @ts-ignore
            ItemContainer: {
                className: 'ms-footer__link__container'
            },
             // @ts-ignore
            Link: link && link.linkUrl && link.linkUrl.destinationUrl && {
                tag: 'a',
                className: 'ms-footer__link',
                href: link.linkUrl.destinationUrl,
                title:link.linkText,
                target: link.openInNewTab ? '_blank' : undefined
            },
            className:footerItemClassName
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _createLinkText(link: ILinkData): React.ReactNode | null {
        if(!link || !link.linkText) {
            return null;
        }

        return (<span className='ms-footer__link__text'>{link.linkText}</span>);
    }

    private _createHeading(heading: IHeadingData): React.ReactNode | null {
        if(!heading || !heading.text || !heading.text.length) {
            return null;
        }

        return (
            <Heading {...heading} className='ms-footer__heading__title' />
        );
    }

    private _createText(text: string): React.ReactNode | null {
        if (!text) {
            return null;
        }
        return <div className='ms-footer-item__text'>{text}</div>;
    }

    private _createImageMarkup(item: IImageData, ariaHiddenAttr?: boolean): React.ReactNode | null {
        if(!item || !item.src || !item.src.length) {
            return null;
        }

        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: `w=24&h=24&m=6`, w: 0, h: 0 },
                lg: { q: `w=24&h=24&m=6`, w: 0, h: 0 }
            },
            lazyload: true
        };
        return <Image {...item} gridSettings={this.props.context.request.gridSettings!} imageSettings={item.imageSettings || defaultImageSettings} loadFailureBehavior='default' aria-hidden={ariaHiddenAttr} />;
    }
}

export default FooterItem;
